import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CHAT_SHARING_STATUS } from '../constants/chatEnums';
import { clearNotificationFor } from '../utils/webPush';
import { deleteLetterboxMessage as deleteLetterboxMessageApi, getChatHistory, getChatMetadata, getLetterbox, markChatSeen, sendMessageToChat } from '../services/chatApi';
import eventBus from '../utils/eventBus';
import { normalizeForDedupe } from '../utils/messageDedupe';
import UserStreamManager from '../utils/UserStreamManager';
import { useAuth } from './AuthContext';
import { useChatList } from './ChatListContext';
import { useEmployeeList } from './EmployeeListContext';

const CurrentChatContext = createContext();

// Unified letterbox visibility: pull view-only SYSTEM-lane items out of a server response
// (history or letterbox snapshot). Human-lane items keep flowing through letterbox_messages
// so the existing optimistic/delete machinery is untouched.
const extractSystemItems = (data) => (data?.letterbox_items || []).filter(it => it.lane === 'system');

// CEL-2093: initial-load failures worth retrying SILENTLY — no response at all (server restarting
// during a deploy, network blip) or a server-side/transient status. 4xx client errors (404 missing
// chat, 403 access) are final. Programming errors (no axios shape) are never retried.
const isTransientLoadError = (err) => {
  if (!err?.response) return Boolean(err?.isAxiosError || err?.request);
  return [408, 425, 429, 500, 502, 503, 504].includes(err.response.status);
};


export const CurrentChatProvider = ({ children }) => {
  // Server-confirmed state
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [letterboxMessages, setLetterboxMessages] = useState([]);
  // Unified letterbox visibility (letterbox_unified_visibility_prd.md): view-only SYSTEM-lane
  // items (announcements / email events / system notes) rendered alongside the human queue.
  // Kept SEPARATE from letterboxMessages so all existing human-lane machinery (optimistic add,
  // index-delete, restore-on-failure) is untouched.
  const [letterboxSystemItems, setLetterboxSystemItems] = useState([]);
  // CEL-2137 (pause = frozen): one-shot prompt set when a send landed in a PAUSED employee's mailbox
  // ({ chatId, waiting, at }); ChatPage renders the Resume modal and clears it.
  const [pausedMailboxPrompt, setPausedMailboxPrompt] = useState(null);

  // Loading/error state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // CEL-2093: initial-load retry state. When loadChat fails with a TRANSIENT error the chat stays in
  // its loading skeleton and is retried with backoff instead of rendering an error banner.
  // `loadRetry` = { since, attempt } while a retry is pending (ChatWindow shows a quiet
  // "still connecting" line only after ~60s), null otherwise.
  const [loadRetry, setLoadRetry] = useState(null);
  const loadRetryTimerRef = useRef(null);
  const loadRetryAttemptRef = useRef(0);
  const loadChatSelfRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [blobNameToUrl, setBlobNameToUrl] = useState({});
  const [isRefreshingMessages, setIsRefreshingMessages] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Streaming/optimistic state
  const [streamingMessage, setStreamingMessage] = useState(null);
  const [completionPending, setCompletionPending] = useState(false);
  const [optimisticMessage, setOptimisticMessage] = useState(null);
  const [agentUpdateHistory, setAgentUpdateHistory] = useState([]);
  const [inFlightAIMessages, setInFlightAIMessages] = useState([]);

  const lastOperatingRef = useRef(false);
  const streamingCompleteRef = useRef(false);
  const completionInProgressRef = useRef(false);
  const latestRequestedChatIdRef = useRef(null);
  const currentChatIdRef = useRef(null);

  // Refs to prevent duplicate WebSocket subscriptions (React Strict Mode)
  const messageCompleteSubscriptionRef = useRef(null);
  const streamChunkSubscriptionRef = useRef(null);
  const interimMessageSubscriptionRef = useRef(null);

  // Ref to prevent duplicate message processing in Strict Mode
  const messageCompleteProcessedRef = useRef(new Set());
  const chatCompletedSubscriptionRef = useRef(null);

  const { updateChatInList } = useChatList();
  const { currentUser } = useAuth();
  const { employees: employeeRows } = useEmployeeList();

  const displayMessages = useMemo(() => {
    const baseMessages = [...messages];

    // Add in-flight AI messages (completed streaming, not yet in server)
    inFlightAIMessages.forEach(msg => {
      baseMessages.push(msg);
    });

    // CEL-1889 append-always: the bubble PINS until server history contains the message
    // (sends now transit the server letterbox before landing in history). Skip the push
    // once history has it — no double render during the reconcile window.
    // NOTE: server history stamps USER messages with the user's NAME/email (Message.from_db_model),
    // never 'You' ('You' is FE-local only) — so match "any non-agent sender with this content".
    // File-send fix (2026-08-31): compare NORMALIZED content — the server rewrites file-tag
    // paths (staging → chat namespace + collision suffix) and escapes reserved keywords
    // between send and history, so exact equality never matched for sends with attachments
    // and the bubble pinned forever (message shown twice). See utils/messageDedupe.js.
    if (optimisticMessage) {
      const optNorm = normalizeForDedupe(optimisticMessage.content);
      if (!messages.some(m => m.messageFrom !== 'CellCog' && normalizeForDedupe(m.content) === optNorm)) {
        baseMessages.push(optimisticMessage);
      }
    }
    return baseMessages;
  }, [messages, inFlightAIMessages, optimisticMessage]);

  // CEL-1889 append-always UX (no bubble→queue→bubble flicker): the optimistic bubble clears
  // ONLY when server history actually contains the message — never at a fixed refresh point,
  // because an idle send now sits in the server letterbox until the run's startup drain.
  useEffect(() => {
    if (!optimisticMessage) return;
    // Server history stamps user messages with the user's NAME/email, never 'You' — match any
    // non-agent sender (same predicate as the displayMessages dedupe above).
    const optNorm = normalizeForDedupe(optimisticMessage.content);
    if (messages.some(m => m.messageFrom !== 'CellCog' && normalizeForDedupe(m.content) === optNorm)) {
      setOptimisticMessage(null);
    }
  }, [messages, optimisticMessage]);

  // Queue display: hide the letterbox row this tab is ALREADY showing as its optimistic bubble.
  // TAIL-only (our just-sent message is the newest row) so every other row keeps its canonical
  // index — deleteLetterboxMessage's index-based API stays exact. A non-tail match (rare
  // multi-sender race) degrades to showing the row in both places briefly, never a wrong delete.
  const displayLetterboxMessages = useMemo(() => {
    if (!optimisticMessage || letterboxMessages.length === 0) return letterboxMessages;
    const last = letterboxMessages[letterboxMessages.length - 1];
    if (normalizeForDedupe(last.content) === normalizeForDedupe(optimisticMessage.content)) return letterboxMessages.slice(0, -1);
    return letterboxMessages;
  }, [letterboxMessages, optimisticMessage]);

  const createMessageKey = useCallback((message, index) => {
    return `${message.messageFrom}_${message.createdAt}_${message.content.length}_${index}`;
  }, []);

  // Improved reconciliation: Clear ALL in-flight on interim refresh
  // Since non-blocking messages are committed to server immediately after delivery,
  // any in-flight messages should be in the next history fetch
  const reconcileInFlightMessages = useCallback((serverMessages) => {
    if (inFlightAIMessages.length === 0) return;

    const serverAIMessages = serverMessages.filter(m => m.messageFrom === 'CellCog');

    // AGGRESSIVE: Just clear all in-flight messages
    // Server history is the source of truth after interim refresh
    setInFlightAIMessages([]);

  }, [inFlightAIMessages.length]);

  const updateMessagesWithDelta = useCallback((newMessages) => {
    setMessages(prev => {
      const result = [];
      let hasChanges = false;

      newMessages.forEach((newMsg, idx) => {
        const newKey = createMessageKey(newMsg, idx);

        const existingIndex = prev.findIndex((oldMsg, oldIdx) =>
          createMessageKey(oldMsg, oldIdx) === newKey
        );

        if (existingIndex !== -1) {
          const existingMsg = prev[existingIndex];

          if (existingMsg.content !== newMsg.content) {
            result.push(newMsg);
            hasChanges = true;
          } else {
            result.push(existingMsg);
          }
        } else {
          result.push(newMsg);
          hasChanges = true;
        }
      });

      if (!hasChanges && result.length === prev.length) {
        return prev;
      }

      return result;
    });
  }, [createMessageKey]);

  // ATOMIC state replacement on chat completion
  const handleChatCompletion = useCallback(async () => {
    if (completionInProgressRef.current) {
      return;
    }

    completionInProgressRef.current = true;

    try {
      window.dispatchEvent(new CustomEvent('chat-completion-starting', {
        detail: { chatId }
      }));

      const historyResponse = await getChatHistory(chatId);

      if (latestRequestedChatIdRef.current && latestRequestedChatIdRef.current !== chatId) {
        return;
      }

      if (historyResponse.data.blob_name_to_url) {
        setBlobNameToUrl(historyResponse.data.blob_name_to_url);
      }

      const serverMessages = historyResponse.data.messages;
      // ATOMIC UPDATE
      setMessages(serverMessages);
      setStreamingMessage(null);
      setInFlightAIMessages([]);
      setAgentUpdateHistory([]);
      setOptimisticMessage(null);
      setLetterboxMessages((historyResponse.data.letterbox_messages || []).map(content => ({
        content,
        localTimestamp: null
      })));
      setLetterboxSystemItems(extractSystemItems(historyResponse.data));
      setCompletionPending(false);
      streamingCompleteRef.current = false;
      messageCompleteProcessedRef.current.clear();

      setCurrentChat(prev => ({
        ...prev,
        sharing_status: historyResponse.data.sharing_status,
        shared_post_id: historyResponse.data.shared_post_id,
        is_app_public: historyResponse.data.is_app_public,
        user: historyResponse.data.user
      }));

      window.dispatchEvent(new CustomEvent('chat-reconciliation-complete', {
        detail: { chatId }
      }));

    } catch (err) {
      console.error('Error handling chat completion:', err);
      setCompletionPending(false);
    } finally {
      completionInProgressRef.current = false;
    }
    // Deps are [chatId] ONLY: the body reads chatId + refs + setState functions, none of which are
    // streaming state. Listing streamingMessage/messages/inFlightAIMessages here recreated this
    // callback on EVERY stream chunk, which tore down and re-added the CHAT_COMPLETED and
    // chat-metadata-updated subscriptions hundreds of times per run — the "effect re-subscription
    // race" that this file's own comments blame for missed completions (2026-07-24).
  }, [chatId]);

  const reconcileChatState = useCallback(async (reason) => {
    if (!chatId) return;

    // CEL-2093: the WS just reconnected (server back after a restart) while the initial load was
    // waiting on its retry timer — load now instead of waiting the timer out.
    if (loadRetryTimerRef.current) {
      clearTimeout(loadRetryTimerRef.current);
      loadRetryTimerRef.current = null;
      loadChatSelfRef.current?.(chatId);
      return;
    }

    try {
      const metadataResponse = await getChatMetadata(chatId);

      if (latestRequestedChatIdRef.current !== chatId) {
        return;
      }

      const serverChat = metadataResponse.data;

      const operatingChanged = currentChat && currentChat.operating !== serverChat.operating;
      const wasOperating = currentChat?.operating;
      const nowComplete = !serverChat.operating;


      if (operatingChanged && wasOperating && nowComplete) {
        lastOperatingRef.current = false;

        setAgentUpdateHistory([]);
        setCompletionPending(true);
        await handleChatCompletion();

        return;
      }

      setCurrentChat(prev => ({
        ...prev,
        ...serverChat
      }));

      lastOperatingRef.current = serverChat.operating;

      if (updateChatInList) {
        updateChatInList(serverChat);
      }

    } catch (err) {
      console.error('[CurrentChat] Reconciliation error:', err);
    }
  }, [chatId, currentChat, handleChatCompletion, updateChatInList]);

  const refreshChatHistory = useCallback(async () => {
    if (!chatId) return;
    // Don't refresh while a message is being sent — prevents race condition where
    // server history doesn't include the just-sent message yet, causing it to
    // be dropped from the messages state by updateMessagesWithDelta.
    if (sending) return;
    try {
      setIsRefreshingMessages(true);
      const historyResponse = await getChatHistory(chatId);

      if (latestRequestedChatIdRef.current !== chatId) {
        return;
      }

      if (historyResponse.data.blob_name_to_url) {
        setBlobNameToUrl(prev => ({ ...prev, ...historyResponse.data.blob_name_to_url }));
      }

      const serverMessages = historyResponse.data.messages;
      setLetterboxMessages((historyResponse.data.letterbox_messages || []).map(content => ({
        content,
        localTimestamp: null
      })));
      setLetterboxSystemItems(extractSystemItems(historyResponse.data));

      // Update committed messages
      updateMessagesWithDelta(serverMessages);

      // Reconcile in-flight (now uses aggressive clear)
      reconcileInFlightMessages(serverMessages);

    } catch (err) {
      console.error('[CurrentChat] Error refreshing chat history:', err);
    } finally {
      setIsRefreshingMessages(false);
    }
  }, [chatId, sending, updateMessagesWithDelta, reconcileInFlightMessages]);

  // CEL-2093: schedule a silent retry of the initial load (2s → 4s → 8s … capped at 30s; only while
  // the tab is visible; the WS reconnection callback also fires it immediately). Returns true.
  const scheduleLoadRetry = useCallback((id) => {
    const attempt = loadRetryAttemptRef.current;
    loadRetryAttemptRef.current = attempt + 1;
    setLoadRetry(prev => ({ since: prev?.since ?? Date.now(), attempt: attempt + 1 }));
    const delay = Math.min(30000, 2000 * Math.pow(2, attempt));
    if (loadRetryTimerRef.current) clearTimeout(loadRetryTimerRef.current);
    const fire = () => {
      loadRetryTimerRef.current = null;
      if (latestRequestedChatIdRef.current !== id) return;
      if (document.visibilityState !== 'visible') {
        // Hidden tab: don't burn the ladder, look again shortly.
        loadRetryTimerRef.current = setTimeout(fire, 5000);
        return;
      }
      loadChatSelfRef.current?.(id);
    };
    loadRetryTimerRef.current = setTimeout(fire, delay);
    return true;
  }, []);

  // Manual "Try again" from the loading state's still-connecting line.
  const retryLoadNow = useCallback(() => {
    const id = latestRequestedChatIdRef.current;
    if (!id) return;
    if (loadRetryTimerRef.current) { clearTimeout(loadRetryTimerRef.current); loadRetryTimerRef.current = null; }
    loadChatSelfRef.current?.(id);
  }, []);

  const loadChat = useCallback(async (id) => {
    if (!id) {

      setCurrentChat(null);
      setMessages([]);
      setLetterboxMessages([]);
      setLetterboxSystemItems([]);
      setChatId(null);
      setBlobNameToUrl({});
      setInitialLoadComplete(false);
      setAgentUpdateHistory([]);
      setStreamingMessage(null);
      setCompletionPending(false);
      setOptimisticMessage(null);
      setInFlightAIMessages([]);

      latestRequestedChatIdRef.current = null;
      currentChatIdRef.current = null;
      streamingCompleteRef.current = false;
      completionInProgressRef.current = false;

      // CEL-1882 (2026-08-28, duplicate-streamed-lines LAYER 2 — the subscription leak):
      // this branch runs on ChatPage unmount (loadChat(null)) BEFORE React executes the
      // [chatId] effects' cleanups. Nulling the refs here made those cleanups no-ops
      // (`if (ref.current)` saw null), so the handlers stayed registered in
      // UserStreamManager forever. Every leave-and-return to chat routes then added one
      // more live CHAT_STREAM_CHUNK handler → every streamed line rendered N× (uniform
      // 2×/3×/4×, reset by refresh). Fix: actually UNSUBSCRIBE before nulling.
      if (messageCompleteSubscriptionRef.current) messageCompleteSubscriptionRef.current();
      if (streamChunkSubscriptionRef.current) streamChunkSubscriptionRef.current();
      if (interimMessageSubscriptionRef.current) interimMessageSubscriptionRef.current();
      if (chatCompletedSubscriptionRef.current) chatCompletedSubscriptionRef.current();
      messageCompleteSubscriptionRef.current = null;
      streamChunkSubscriptionRef.current = null;
      interimMessageSubscriptionRef.current = null;
      chatCompletedSubscriptionRef.current = null;
      messageCompleteProcessedRef.current.clear();

      // CEL-2093: a pending initial-load retry belongs to the chat we just left.
      if (loadRetryTimerRef.current) { clearTimeout(loadRetryTimerRef.current); loadRetryTimerRef.current = null; }
      loadRetryAttemptRef.current = 0;
      setLoadRetry(null);

      return;
    }

    if (chatId === id && initialLoadComplete) {
      return;
    }

    // CEL-2093: switching to a DIFFERENT chat resets the retry ladder; a retry of the same chat
    // keeps its attempt count so the backoff keeps growing.
    if (loadRetryTimerRef.current) { clearTimeout(loadRetryTimerRef.current); loadRetryTimerRef.current = null; }
    if (latestRequestedChatIdRef.current !== id) {
      loadRetryAttemptRef.current = 0;
      setLoadRetry(null);
    }

    latestRequestedChatIdRef.current = id;
    currentChatIdRef.current = id;

    setChatId(id);
    setInitialLoadComplete(false);
    setAgentUpdateHistory([]);
    setStreamingMessage(null);
    setCompletionPending(false);
    // Send-then-switch bleed fix: the optimistic bubble from a JUST-SENT message in the
    // previous chat must not survive into this chat. CEL-1889 pins the bubble until server
    // history contains it — which never happens against a DIFFERENT chat's history, so it
    // rendered as a ghost "last message" at the bottom of the newly opened chat until refresh.
    setOptimisticMessage(null);
    setInFlightAIMessages([]);
    setLetterboxMessages([]);
    setLetterboxSystemItems([]);

    streamingCompleteRef.current = false;
    completionInProgressRef.current = false;
    messageCompleteProcessedRef.current.clear();

    let retryScheduled = false;
    try {
      setLoading(true);
      setError(null);

      const metadataResponse = await getChatMetadata(id);

      if (latestRequestedChatIdRef.current !== id) {
        return;
      }

      const chatData = metadataResponse.data;
      setCurrentChat(chatData);

      lastOperatingRef.current = chatData.operating;

      try {
        const historyResponse = await getChatHistory(id);

        if (latestRequestedChatIdRef.current !== id) {
          return;
        }

        if (historyResponse.data.chat_id && historyResponse.data.chat_id !== id) {
          return;
        }

        if (historyResponse.data.blob_name_to_url) {
          setBlobNameToUrl(historyResponse.data.blob_name_to_url);
        } else {
          setBlobNameToUrl({});
        }

        setCurrentChat(prev => ({
          ...prev,
          sharing_status: historyResponse.data.sharing_status,
          shared_post_id: historyResponse.data.shared_post_id,
          is_app_public: historyResponse.data.is_app_public,
          user: historyResponse.data.user,
          // Credits ride in with history (the AI-employee current-shift divider seeds its live meter
          // from this so it appears WITH the conversation, not via a separate late fetch).
          credits: historyResponse.data.credits ?? prev?.credits
        }));

        setMessages(historyResponse.data.messages);
        setLetterboxMessages((historyResponse.data.letterbox_messages || []).map(content => ({
          content,
          localTimestamp: null
        })));
        setLetterboxSystemItems(extractSystemItems(historyResponse.data));

        // CEL-1889 append-always: a BRAND-NEW chat's first message is letterboxed until the run's
        // startup drain — on this load, history has no user message yet while the letterbox holds
        // it. Seed it as the optimistic bubble (the pin/reconcile/tail-filter machinery takes over:
        // renders as a normal sent bubble, hidden from the queue strip, clears when history has it).
        // Reachable since 2026-09-01: history used to 404 for a chat with no transcript yet, so
        // this block never ran and new chats opened blank for a few seconds
        // (agent_docs/new_chat_first_message_instant_render_design.md).
        // Employees: ONLY the setup shift (self-anchored: its id IS the employee id) — the hire
        // brief the owner just typed renders as their sent bubble. Later shifts keep their richer
        // "queued for the employee" row UI.
        const _histMsgs = historyResponse.data.messages || [];
        const _lbRows = historyResponse.data.letterbox_messages || [];
        const _empId = metadataResponse.data?.ai_employee_id;
        const _isSetupShift = Boolean(_empId) && metadataResponse.data?.id === _empId;
        if (
          (!_empId || _isSetupShift) &&
          _lbRows.length > 0 &&
          !_histMsgs.some(m => m.messageFrom !== 'CellCog')
        ) {
          setOptimisticMessage({
            messageFrom: 'You',
            content: _lbRows[_lbRows.length - 1],
            createdAt: new Date().toISOString(),
            isOptimistic: true
          });
        }

        setInitialLoadComplete(true);
        loadRetryAttemptRef.current = 0;
        setLoadRetry(null);

        // Auto mark-seen: user just navigated to this chat
        markChatSeen(id).catch(() => {});
        clearNotificationFor({ chatId: id }); // opening the chat clears its system notification (Sep 9 2026)
      } catch (historyErr) {
        console.error('Error loading chat history:', historyErr);

        // CEL-2093: a transient history failure (server mid-restart) must not render the chat with
        // an EMPTY transcript — hand it to the outer retry path.
        if (isTransientLoadError(historyErr)) throw historyErr;

        if (historyErr.response &&
          historyErr.response.status === 403 &&
          historyErr.response.data.detail === "CellCog access not enabled for this user") {
          setError('CellCog access not enabled for this user');
        } else {
          setMessages([]);
          setLetterboxMessages([]);
          setLetterboxSystemItems([]);
          setBlobNameToUrl({});
        }
      }

    } catch (err) {
      console.error('Error loading chat:', err);

      // CEL-2093: the user moved to another chat while this load was in flight — that chat's own
      // loadChat owns `loading` and the retry ladder now; this stale failure must touch neither.
      if (latestRequestedChatIdRef.current !== id) {
        retryScheduled = true;
      } else if (err.response &&
        err.response.status === 403 &&
        err.response.data.detail === "CellCog access not enabled for this user") {
        setError('CellCog access not enabled for this user');
      } else if (isTransientLoadError(err)) {
        // CEL-2093: server restarting / network blip. Stay in the loading skeleton and retry
        // silently. No banner: the server comes back and the chat is unaffected.
        retryScheduled = scheduleLoadRetry(id);
      }
      // Any other failure (404, non-access 403, 401 after refresh): `error` stays null and
      // ChatWindow's empty state reads "Chat not found or you don't have access".
    } finally {
      if (!retryScheduled) setLoading(false);
    }
  }, [chatId, initialLoadComplete, scheduleLoadRetry]);

  useEffect(() => { loadChatSelfRef.current = loadChat; }, [loadChat]);

  const sendMessage = async (message, approvedToolSlugs = [], editorBlobUrls = {}) => {
    if (!currentChat || !message.trim()) {
      return { success: false, shouldRestore: false };
    }

    // Send-then-switch race guard: capture the chat this send belongs to. The user can
    // navigate to ANOTHER chat while the awaits below are in flight — every post-await
    // state write must verify we're still on this chat, otherwise chat A's metadata,
    // history and queue get written into chat B's freshly loaded state.
    const sendChatId = currentChat.id;
    const stillOnSendChat = () => currentChatIdRef.current === sendChatId;

    // Ensure WebSocket is connected before sending message for streaming updates
    await UserStreamManager.ensureConnected(3000);

    // The server letterboxes when operating OR during a Human Computer approval turn
    // (chat_message_service.deliver_user_message). Mirror BOTH flags — checking only
    // `operating` made HC-approval sends take the idle branch, so the queued message
    // vanished until refresh (CEL-664 bug 1).
    const isOperating = currentChat.operating || currentChat.human_computer_turn;
    if (isOperating) {
      setLetterboxMessages(prev => [...prev, {
        content: message,
        localTimestamp: new Date().toISOString()
      }]);
    } else {
      const optimistic = {
        messageFrom: 'You',
        content: message,
        createdAt: new Date().toISOString(),
        isOptimistic: true
      };
      setOptimisticMessage(optimistic);
    }

    if (editorBlobUrls && Object.keys(editorBlobUrls).length > 0) {
      setBlobNameToUrl(prev => ({
        ...prev,
        ...editorBlobUrls
      }));
    }

    try {
      setSending(true);
      setError(null);

      // Unified settings pipeline: approvals persist via settings when changed; the send carries the message only.
      const response = await sendMessageToChat(sendChatId, message);

      // The chat list row is keyed by chat id — always safe to update regardless of
      // which chat is currently open.
      if (updateChatInList) {
        updateChatInList(response.data);
      }

      if (stillOnSendChat()) {
        // MERGE, never replace (Sep 23 2026): the send response is ChatMeta, which carries no `user`
        // (the owner rides in with history). Replacing dropped it, so isOwner read false and every
        // connect card flipped to its read-only "Only the chat owner can set this up" face until a
        // refresh re-fetched history.
        setCurrentChat(prev => (prev && prev.id === response.data?.id ? { ...prev, ...response.data } : response.data));
        lastOperatingRef.current = response.data.operating;
        // CEL-2137: the employee is PAUSED — the server queued the message to its mailbox instead
        // of waking it. Drop the optimistic bubble (the message shows in the queue strip, like any
        // letterboxed send) and raise the one-shot prompt ChatPage turns into the Resume modal.
        if (response.data?.paused_mailbox) {
          setOptimisticMessage(null);
          setPausedMailboxPrompt({
            chatId: sendChatId,
            waiting: response.data.mailbox_waiting || 1,
            at: Date.now(),
          });
        }
      }

      if (!isOperating && stillOnSendChat()) {
        // Fetch authoritative history from server — the user's message is already
        // committed to DB by the API handler, so server history includes it.
        // This replaces all optimistic/in-flight state with server truth,
        // fixing any stale inFlightAIMessages from previous operations.
        try {
          const historyResponse = await getChatHistory(sendChatId);
          if (!stillOnSendChat()) {
            // User switched chats while this fetch was in flight. The send itself
            // succeeded — just do NOT write this chat's history into the newly
            // loaded chat's state (this was the "history bleed" half of the bug).
            return { success: true, shouldRestore: false };
          }
          if (historyResponse.data.blob_name_to_url) {
            setBlobNameToUrl(prev => ({ ...prev, ...historyResponse.data.blob_name_to_url }));
          }
          setMessages(historyResponse.data.messages);
          // Re-seed the queue from server truth too (CEL-664): some idle-looking sends still
          // letterbox server-side (employee wake-OFF, closed-mid-send races) — without this
          // the queued message vanished until refresh.
          setLetterboxMessages((historyResponse.data.letterbox_messages || []).map(content => ({
            content,
            localTimestamp: null
          })));
          setLetterboxSystemItems(extractSystemItems(historyResponse.data));
          setInFlightAIMessages([]);
          // CEL-1889 append-always: do NOT clear the optimistic bubble here — this history
          // fetch races the run's startup drain, so the message may not be in history yet.
          // The reconcile effect clears it the moment server history contains it.
          messageCompleteProcessedRef.current.clear();
        } catch (historyErr) {
          // Fallback: if history fetch fails, use the old manual append approach
          console.error('Failed to fetch history after send, using fallback:', historyErr);
          if (stillOnSendChat()) {
            const confirmedMessage = {
              messageFrom: 'You',
              content: message,
              createdAt: new Date().toISOString(),
              isOptimistic: false
            };
            setMessages(prev => [...prev, confirmedMessage]);
            setOptimisticMessage(null);
          }
        }
      }

      return { success: true, shouldRestore: false };
    } catch (err) {
      console.error('Error sending message:', err);

      if (stillOnSendChat()) {
        if (isOperating) {
          setLetterboxMessages(prev => prev.filter(m => m.content !== message));
        } else {
          setOptimisticMessage(null);
        }
      }

      if (err.response?.status === 402) {
        return {
          success: false,
          shouldRestore: true,
          errorType: 'insufficient_credits',
          errorDetail: err.response.data.detail
        };
      }

      if (err.response?.status === 429) {
        const detail = err.response?.data?.detail;
        return {
          success: false,
          shouldRestore: true,
          errorType: 'max_concurrency',
          errorDetail: detail
        };
      }

      // CEL-2093: send failures surface at the COMPOSER (action-time, under the restored text),
      // never as a chat-level banner. LexicalMessageInput renders `errorMessage` inline.
      let errorMessage;
      if (err.response &&
        err.response.status === 403 &&
        err.response.data.detail === "CellCog access not enabled for this user") {
        errorMessage = 'CellCog access is not enabled for this account.';
      } else if (err.response && err.response.status === 403) {
        errorMessage = "This message couldn't be sent to this chat.";
      } else if (!err.response) {
        errorMessage = "Couldn't reach CellCog. Your message is still here; try again in a moment.";
      } else {
        errorMessage = "Couldn't send that message. Please try again.";
      }

      return { success: false, shouldRestore: true, errorType: 'send_failed', errorMessage };
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    currentChatIdRef.current = chatId;
  }, [chatId]);

  useEffect(() => {
    const unsubscribe = UserStreamManager.addReconciliationCallback(reconcileChatState);
    return () => {
      unsubscribe();
    };
  }, [reconcileChatState]);

  // TERMINAL-STATE TAP — listen DIRECTLY to CHAT_STATUS_UPDATE (bug 2026-07-24: status bar stuck
  // on "Working" after a run crashed).
  //
  // This is the SAME event that already reliably stops the sidebar pulse and plays the completion
  // sound (ChatListContext) — which is the proof the signal arrives and is processed. The status
  // bar was the ONLY consumer hearing it second-hand: CHAT_STATUS_UPDATE → ChatListContext →
  // eventBus('chat-metadata-updated') → an effect that (a) bails out entirely while `currentChat`
  // is null and (b) lists `currentChat` + `handleChatCompletion` in its deps, so it tears down and
  // re-registers on every metadata tick. ChatListContext's own subscription, by contrast, is
  // registered once ([currentUser, updateChatInList], and updateChatInList has [] deps) — which is
  // exactly why the sound never misses and the bar does.
  //
  // So: tap the event at the source for the one field set that must never be missed. Subscription
  // is created ONCE and filters through currentChatIdRef, so nothing can re-register it away.
  //
  // MERGE-ONLY, and deliberately so: it writes just the three run-state fields the bar reads from
  // the SAME payload the backend already publishes. It never fetches, never overwrites chat
  // metadata, and never invents state — that was the failure mode of the reverted poll (a stale
  // snapshot resurrecting `human_computer_turn` and manufacturing a "Commands completed" card).
  // Idempotent: re-applying the same pushed values is a no-op.
  useEffect(() => {
    const unsubscribe = UserStreamManager.subscribe('CHAT_STATUS_UPDATE', (data) => {
      if (!data || data.chat_id !== currentChatIdRef.current) return;

      setCurrentChat(prev => {
        if (!prev) return prev;
        const updates = { ...prev };
        if ('operating' in data) updates.operating = data.operating;
        if ('stop_reason' in data) updates.stop_reason = data.stop_reason;
        // Warm/cold meter input (2026-09-02): stamped in the SAME stop-time payload as stop_reason,
        // so the header / shift-divider flame-or-snowflake flips the moment the run stops.
        if ('cache_warm_until' in data) updates.cache_warm_until = data.cache_warm_until;
        if ('human_computer_turn' in data) updates.human_computer_turn = data.human_computer_turn;
        // Badge inputs for the declarative mark-seen rule (2026-07-29): the backend includes
        // attention_at in every stop-time CHAT_STATUS_UPDATE and user_last_seen_at in the
        // mark-seen broadcast. Merging them HERE (registered once, filters by ref — provably
        // never misses, per the 2026-07-24 notes above) guarantees the rule's effect deps fire
        // even if the chat-metadata-updated hop drops a tick during re-registration.
        if ('attention_at' in data) updates.attention_at = data.attention_at;
        if ('user_last_seen_at' in data) updates.user_last_seen_at = data.user_last_seen_at;
        return updates;
      });

      // Keep the completion bookkeeping ref honest so the eventBus/CHAT_COMPLETED paths that
      // read it still make correct wasOperating→nowComplete decisions.
      if ('operating' in data && !data.operating) {
        lastOperatingRef.current = false;
      }
    });

    return unsubscribe;
  }, []);

  // NOTE (2026-07-24): a 20s "self-heal" metadata poll lived here briefly and was REMOVED.
  // reconcileChatState ends in a full overwrite (`{...prev, ...serverChat}`), so a poll response
  // already in flight when a WebSocket event lands would clobber the fresher pushed state with an
  // older snapshot — resurrecting `operating: true` (status bar snapping back to "Working") and
  // `human_computer_turn: true` (which re-triggered the HC bootstrap fetch and surfaced a
  // "Commands completed" chiclet for batches that were never meant to be shown).
  // The backend's push is authoritative and correctly timed; the real defect was the client
  // DISCARDING it (fixed in the CHAT_COMPLETED handler below). Reconciliation stays event-driven
  // — WS reconnect and tab-return — plus the user can always refresh.
  // See agent_docs/stuck_working_status_bar_diagnosis.md.

  // Handle CHAT_MESSAGE_COMPLETE
  useEffect(() => {
    if (!chatId) return;

    if (messageCompleteSubscriptionRef.current) {
      return messageCompleteSubscriptionRef.current;
    }

    const unsubscribe = UserStreamManager.subscribe('CHAT_MESSAGE_COMPLETE', (data) => {
      if (data.chat_id !== currentChatIdRef.current) {
        return;
      }

      // Skip during completion — handleChatCompletion does atomic state replacement,
      // and late MESSAGE_COMPLETE events would re-add stale entries to inFlightAIMessages
      if (completionInProgressRef.current) {
        return;
      }

      setStreamingMessage(prev => {
        if (prev && prev.content) {
          const messageKey = `${prev.startedAt}_${prev.content.length}_${prev.content.substring(0, 20)}`;

          if (!messageCompleteProcessedRef.current.has(messageKey)) {
            messageCompleteProcessedRef.current.add(messageKey);

            const completedMessage = {
              messageFrom: 'CellCog',
              content: prev.content,
              createdAt: prev.startedAt || new Date().toISOString(),
              isOptimistic: true,
              isInFlight: true
            };


            setInFlightAIMessages(inFlight => [...inFlight, completedMessage]);

            if (messageCompleteProcessedRef.current.size > 20) {
              const keys = Array.from(messageCompleteProcessedRef.current);
              messageCompleteProcessedRef.current = new Set(keys.slice(-20));
            }
          }
        }
        return null;
      });
    });

    messageCompleteSubscriptionRef.current = unsubscribe;

    return () => {
      if (messageCompleteSubscriptionRef.current) {
        messageCompleteSubscriptionRef.current();
        messageCompleteSubscriptionRef.current = null;
      }
    };
  }, [chatId]);

  // Handle CHAT_STREAM_CHUNK
  useEffect(() => {
    if (!chatId) return;

    if (streamChunkSubscriptionRef.current) {
      return streamChunkSubscriptionRef.current;
    }
    const unsubscribe = UserStreamManager.subscribe('CHAT_STREAM_CHUNK', (data) => {
      if (data.chat_id !== currentChatIdRef.current) {
        return;
      }

      if (streamingCompleteRef.current) {
        return;
      }


      if (data.message_type === 'MESSAGE') {
        setAgentUpdateHistory([]);

        setStreamingMessage(prev => {
          if (!prev) {
            return {
              content: data.text,
              startedAt: new Date().toISOString()
            };
          } else {
            return {
              ...prev,
              content: prev.content + data.text
            };
          }
        });
      }
      else if (data.message_type === 'AGENT_UPDATE') {
        const newUpdate = {
          content: data.text,
          timestamp: new Date().toISOString()
        };

        setAgentUpdateHistory(prev => {
          // Skip the initial "Agent has started working..." placeholder when
          // updates already exist (e.g., after HC turn resumes operation).
          if (prev.length > 0 && data.text === 'Agent has started working...') {
            return prev;
          }
          return [newUpdate, ...prev];
        });
      }

      if (data.blob_urls && Object.keys(data.blob_urls).length > 0) {
        setBlobNameToUrl(prev => ({
          ...prev,
          ...data.blob_urls
        }));
      }
    });

    streamChunkSubscriptionRef.current = unsubscribe;

    return () => {
      if (streamChunkSubscriptionRef.current) {
        streamChunkSubscriptionRef.current();
        streamChunkSubscriptionRef.current = null;
      }
    };
  }, [chatId]);

  // Handle CHAT_INTERIM_MESSAGE
  useEffect(() => {
    if (!chatId) return;

    if (interimMessageSubscriptionRef.current) {
      return interimMessageSubscriptionRef.current;
    }
    const unsubscribe = UserStreamManager.subscribe('CHAT_INTERIM_MESSAGE', (data) => {
      if (data.chat_id !== currentChatIdRef.current) {
        return;
      }

      if (data.action === 'refresh_history') {
        refreshChatHistory();
      }
    });

    interimMessageSubscriptionRef.current = unsubscribe;

    return () => {
      if (interimMessageSubscriptionRef.current) {
        interimMessageSubscriptionRef.current();
        interimMessageSubscriptionRef.current = null;
      }
    };
  }, [chatId, refreshChatHistory]);

  // Handle CHAT_COMPLETED — direct, reliable completion signal
  // This bypasses the indirect CHAT_STATUS_UPDATE → ChatListContext → eventBus path,
  // which can miss completion due to effect re-subscription races.
  useEffect(() => {
    if (!chatId) return;

    if (chatCompletedSubscriptionRef.current) {
      return chatCompletedSubscriptionRef.current;
    }

    const unsubscribe = UserStreamManager.subscribe('CHAT_COMPLETED', (data) => {
      if (data.chat_id !== currentChatIdRef.current) {
        return;
      }

      // Skip HC turn pauses — operation will resume after HC commands resolve.
      // stop_operation() fires CHAT_COMPLETED even during HC turns, but we must
      // NOT reconcile history because the conversation is not truly complete.
      if (data.human_computer_turn) {
        lastOperatingRef.current = false;
        return;
      }

      // TERMINAL STATE FIRST — before any in-flight guard (bug 2026-07-24: status bar stuck on
      // "Working" after a crashed run, see agent_docs/stuck_working_status_bar_diagnosis.md).
      // The backend publishes CHAT_STATUS_UPDATE milliseconds before CHAT_COMPLETED, and that
      // first event ALSO kicks off handleChatCompletion (via ChatListContext → eventBus). So a
      // completion is routinely already in flight when this event lands. When the guard below sat
      // above this merge, the terminal state was silently dropped and the bar kept operating=true
      // FOREVER (Redis pub/sub has no replay). This merge is idempotent and cheap — only the
      // expensive history reconciliation needs the guard.
      lastOperatingRef.current = false;
      // handleChatCompletion fetches HISTORY only, never chat metadata — without this merge the
      // status bar kept a stale/null stop_reason and flashed "Something went wrong" until a manual
      // refresh re-fetched ChatMeta (bug 2026-07-20).
      setCurrentChat(prev => {
        if (!prev) return prev;
        const updates = { ...prev, operating: false };
        if ('stop_reason' in data) updates.stop_reason = data.stop_reason;
        if ('cache_warm_until' in data) updates.cache_warm_until = data.cache_warm_until;
        if ('human_computer_turn' in data) updates.human_computer_turn = data.human_computer_turn;
        return updates;
      });

      // Guard: don't start a SECOND history reconciliation on top of one already running.
      if (completionInProgressRef.current) {
        return;
      }

      setAgentUpdateHistory([]);
      setCompletionPending(true);
      handleChatCompletion();
    });

    chatCompletedSubscriptionRef.current = unsubscribe;

    return () => {
      if (chatCompletedSubscriptionRef.current) {
        chatCompletedSubscriptionRef.current();
        chatCompletedSubscriptionRef.current = null;
      }
    };
  }, [chatId, handleChatCompletion]);

  // Auto mark-seen — ONE declarative rule: tab visible AND attention_at > user_last_seen_at
  // → mark seen. Evaluated at two moments:
  //   (a) immediately when the inputs change (deps include attention_at / user_last_seen_at) —
  //       covers "chat completes WHILE the user is looking at it". This was the 2026-07-29
  //       regression fix: the imperative markChatSeen calls in the chat-metadata-updated handler
  //       went dead when the 2026-07-24 terminal-state tap started clearing lastOperatingRef
  //       FIRST (tap subscribes before ChatListContext — child effects run first, and
  //       UserStreamManager dispatches its handler Set in insertion order), so that handler's
  //       wasOperating was false on every real completion.
  //   (b) on visibilitychange — user returns to a tab where the chat completed while hidden
  //       (possibly with the CHAT_COMPLETED WebSocket event missed, e.g. connection dropped).
  // A hidden tab NEVER marks seen — the unseen reminder email must stay legitimate.
  // Idempotent, no loop: the seen broadcast merges user_last_seen_at back in, the rule goes false.
  useEffect(() => {
    if (!chatId || !currentChat) return;

    const markSeenIfNeeded = () => {
      if (document.hidden) return;
      const attentionAt = currentChat.attention_at;
      const lastSeenAt = currentChat.user_last_seen_at;
      if (attentionAt) {
        const needsAttention = !lastSeenAt || new Date(attentionAt) > new Date(lastSeenAt);
        if (needsAttention) {
          markChatSeen(chatId).catch(() => {});
          clearNotificationFor({ chatId });
        }
      }
    };

    // Inputs changed while the tab may already be visible — evaluate the rule now.
    markSeenIfNeeded();

    document.addEventListener('visibilitychange', markSeenIfNeeded);
    return () => document.removeEventListener('visibilitychange', markSeenIfNeeded);
  }, [chatId, currentChat?.attention_at, currentChat?.user_last_seen_at]);

  useEffect(() => {
    if (!streamingMessage) {
      streamingCompleteRef.current = false;
    }
  }, [streamingMessage]);

  useEffect(() => {
    if (!chatId || !currentChat) return;

    const metadataUnsubscribe = eventBus.on('chat-metadata-updated', ({ chatId: updatedChatId, chat: updatedChat }) => {
      if (updatedChatId === chatId) {
        const hasNameChanged = currentChat && updatedChat.name !== currentChat.name && updatedChat.name;
        const hasLucideIconChanged = currentChat && !currentChat.lucide_icon && updatedChat.lucide_icon;
        const hasThumbnailChanged = currentChat && !currentChat.thumbnail_url && updatedChat.thumbnail_url;

        if (hasNameChanged || hasLucideIconChanged || hasThumbnailChanged) {
          setCurrentChat(prev => ({
            ...prev,
            name: updatedChat.name,
            lucide_icon: updatedChat.lucide_icon,
            thumbnail_url: updatedChat.thumbnail_url
          }));
        }

        // PARTIAL broadcasts are NOT status transitions (2026-09-01, prod repro of the
        // new-chat "No messages yet" bug): ChatListContext re-emits EVERY CHAT_STATUS_UPDATE here,
        // including the mark-seen broadcast ({chat_id, user_last_seen_at}) and other field-only
        // updates that carry no `operating`. `!undefined` read as "complete", so opening an
        // operating chat (loadChat → markChatSeen right after the history load) fired
        // handleChatCompletion, which re-fetched history and cleared optimisticMessage — wiping the
        // letterboxed first message a brand-new chat had JUST seeded as its sent bubble (history is
        // still empty pre-drain, so the window went blank while the queue strip showed the message).
        // A completion is only ever signalled by an explicit boolean.
        if (typeof updatedChat.operating !== 'boolean') return;

        const wasOperating = lastOperatingRef.current;
        const nowComplete = !updatedChat.operating;

        if (wasOperating && nowComplete && !updatedChat.human_computer_turn) {
          // Normal completion — operation finished, not an HC turn pause
          lastOperatingRef.current = false;

          setAgentUpdateHistory([]);
          setCompletionPending(true);
          handleChatCompletion();

          // Mark-seen intentionally NOT here (2026-07-29): the call that lived here was dead —
          // the terminal-state tap always clears lastOperatingRef before this handler runs, so
          // wasOperating never held on a real completion. It also lacked a document.hidden
          // guard. Seen-marking is now the declarative visibility+attention effect below.
        } else if (wasOperating && nowComplete && updatedChat.human_computer_turn) {
          // Operation ended for HC turn — don't trigger completion.
          // Operation will resume after HC commands are resolved.
          lastOperatingRef.current = false;
          // Mark-seen handled by the declarative visibility+attention effect (HC pauses stamp
          // attention_at via stop_operation, so the rule covers this branch too).
        } else if (lastOperatingRef.current !== updatedChat.operating) {
          lastOperatingRef.current = updatedChat.operating;
        }

        setCurrentChat(prev => {
          const updates = { ...prev };

          if ('operating' in updatedChat) updates.operating = updatedChat.operating;
          if ('stop_reason' in updatedChat) updates.stop_reason = updatedChat.stop_reason;
          if ('human_computer_turn' in updatedChat) updates.human_computer_turn = updatedChat.human_computer_turn;

          if ('approved_tool_slugs' in updatedChat && updatedChat.approved_tool_slugs !== undefined) {
            updates.approved_tool_slugs = updatedChat.approved_tool_slugs;
          }
          if ('project_id' in updatedChat && updatedChat.project_id !== undefined) {
            updates.project_id = updatedChat.project_id;
          }
          if ('project_name' in updatedChat && updatedChat.project_name !== undefined) {
            updates.project_name = updatedChat.project_name;
          }
          if ('general_agent_role_id' in updatedChat && updatedChat.general_agent_role_id !== undefined) {
            updates.general_agent_role_id = updatedChat.general_agent_role_id;
          }
          if ('general_agent_role_title' in updatedChat && updatedChat.general_agent_role_title !== undefined) {
            updates.general_agent_role_title = updatedChat.general_agent_role_title;
          }
          // Mode × Tier: settings broadcasts carry the merged (pending-aware) value so
          // the composer selector updates live like every other setting chip.
          if ('chat_mode' in updatedChat && updatedChat.chat_mode !== undefined) {
            updates.chat_mode = updatedChat.chat_mode;
          }
          if ('chat_tier' in updatedChat && updatedChat.chat_tier !== undefined) {
            updates.chat_tier = updatedChat.chat_tier;
          }
          if ('lucide_icon' in updatedChat && updatedChat.lucide_icon !== undefined) {
            updates.lucide_icon = updatedChat.lucide_icon;
          }
          if ('attention_at' in updatedChat) {
            updates.attention_at = updatedChat.attention_at;
          }
          if ('user_last_seen_at' in updatedChat) {
            updates.user_last_seen_at = updatedChat.user_last_seen_at;
          }

          return updates;
        });
      }
    });

    return () => {
      metadataUnsubscribe();
    };
  }, [chatId, currentChat, handleChatCompletion]);

  useEffect(() => {
    const handleAnimationComplete = (event) => {

      if (event.detail.chatId !== chatId || event.detail.chatId !== currentChatIdRef.current) {
        return;
      }

      if (streamingMessage) {
        setStreamingMessage(null);
      }
    };

    window.addEventListener('streaming-animation-complete', handleAnimationComplete);

    return () => {
      window.removeEventListener('streaming-animation-complete', handleAnimationComplete);
    };
  }, [chatId, streamingMessage]);

  // ===== Letterbox realtime (CEL-664, agent_docs/letterbox_realtime_prd.md) =====
  // LETTERBOX_CHANGED is a content-free WS notification published by the Letterbox model on
  // every message mutation (add / delete / transfer / consumption). Each client fetches the
  // authoritative snapshot itself — signed URLs are per-viewer, so the event can't carry them.
  const letterboxRefreshTimerRef = useRef(null);

  const refreshLetterbox = useCallback(async (targetChatId) => {
    try {
      const res = await getLetterbox(targetChatId);
      if (currentChatIdRef.current !== targetChatId) return; // switched away mid-fetch
      if (res.data?.blob_name_to_url) {
        setBlobNameToUrl(prev => ({ ...prev, ...res.data.blob_name_to_url }));
      }
      setLetterboxMessages(prev => {
        // Wholesale replace with server truth; keep localTimestamp for entries this tab
        // sent itself (its own messages keep "Just now" instead of "Queued").
        const localTs = new Map(prev.map(m => [m.content, m.localTimestamp]));
        // Server queued_at (unified visibility) is authoritative when present; the local
        // timestamp remains the fallback for entries queued before meta stamping.
        const humanItems = (res.data?.letterbox_items || []).filter(it => it.lane === 'human');
        const serverTs = new Map(humanItems.map(it => [it.content, it.queued_at]));
        return (res.data?.pending_messages || []).map(content => ({
          content,
          localTimestamp: serverTs.get(content) || localTs.get(content) || null
        }));
      });
      setLetterboxSystemItems(extractSystemItems(res.data));
    } catch (err) {
      console.error('Failed to refresh letterbox:', err);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = UserStreamManager.subscribe('LETTERBOX_CHANGED', (data) => {
      if (!data || data.chat_id !== currentChatIdRef.current) return;
      // Trailing debounce: a burst of mutations costs one fetch.
      if (letterboxRefreshTimerRef.current) clearTimeout(letterboxRefreshTimerRef.current);
      letterboxRefreshTimerRef.current = setTimeout(() => refreshLetterbox(data.chat_id), 300);
    });
    return () => {
      unsubscribe();
      if (letterboxRefreshTimerRef.current) clearTimeout(letterboxRefreshTimerRef.current);
    };
  }, [refreshLetterbox]);

  const deleteLetterboxMessage = useCallback(async (index) => {
    if (!currentChat?.id) return;

    const removed = letterboxMessages[index];
    if (!removed) return;

    // Optimistic removal
    setLetterboxMessages(prev => prev.filter((_, i) => i !== index));

    try {
      await deleteLetterboxMessageApi(currentChat.id, index);
    } catch (err) {
      // Restore on failure (unless 404 — message was already consumed, which is fine)
      if (err.response?.status !== 404) {
        setLetterboxMessages(prev => {
          const restored = [...prev];
          restored.splice(index, 0, removed);
          return restored;
        });
        console.error('Failed to delete letterbox message:', err);
      }
    }
  }, [currentChat?.id, letterboxMessages]);

  // CEL-3474: swap a just-deleted message for the server's placeholder bubble, in place (no history refetch).
  // Matched on createdAt among the human's bubbles: the same key the delete selected it by.
  const applyDeletedMessage = useCallback((deletedMessage) => {
    if (!deletedMessage?.createdAt) return;
    setMessages(prev => prev.map(m => (
      m.messageFrom !== 'CellCog' && m.createdAt === deletedMessage.createdAt ? { ...m, ...deletedMessage } : m
    )));
  }, []);

  const isOwner = useMemo(() =>
    currentChat?.user?.firebase_uid === currentUser?.uid,
    [currentChat?.user?.firebase_uid, currentUser?.uid]
  );

  // Employee access (CEL-3635, utils/employeeAccess.js): in an AI employee's chat, the viewer's employee row (it carries
  // viewer_access + can) so chat surfaces ask canDo(chatEmployee, capability, { isOwner }) instead of isOwner: a member
  // with Team access or a manager acts as the owner there. null in a regular chat, which stays owner-only on isOwner.
  const chatEmployee = useMemo(() => {
    const anchorId = currentChat?.ai_employee_id;
    if (!anchorId) return null;
    return (employeeRows || []).find((e) => (e.employee_id || e.id) === anchorId) || null;
  }, [currentChat?.ai_employee_id, employeeRows]);

  const isSharedChat = useMemo(() =>
    currentChat?.sharing_status === CHAT_SHARING_STATUS.SHARED || currentChat?.is_app_public === true,
    [currentChat?.sharing_status, currentChat?.is_app_public]
  );

  const isViewingSharedChat = useMemo(() =>
    isSharedChat && !isOwner,
    [isSharedChat, isOwner]
  );

  // `chatId` (optional): apply only while that chat is still the current one, so a write that lands after
  // the user switched chats never paints the old chat's config onto the new one. Only calls the state
  // setter, so its identity never changes (CEL-3662 review B1).
  const updateChatCapabilities = useCallback((fields, chatId = null) => {
    setCurrentChat(prev => (prev && (!chatId || prev.id === chatId) ? ({ ...prev, ...fields }) : prev));
  }, []);

  const chatOwnerName = useMemo(() =>
    currentChat?.user?.name || currentChat?.user?.email || 'Unknown User',
    [currentChat?.user?.name, currentChat?.user?.email]
  );

  return (
    <CurrentChatContext.Provider value={{
      currentChat,
      messages,
      displayMessages,
      letterboxMessages: displayLetterboxMessages,
      letterboxSystemItems,
      inFlightAIMessages,
      loading,
      loadRetry,
      retryLoadNow,
      error,
      sending,
      blobNameToUrl,
      isRefreshingMessages,
      completionPending,
      streamingMessage,
      optimisticMessage,
      agentUpdateHistory,
      sharingStatus: currentChat?.sharing_status || CHAT_SHARING_STATUS.NOT_SHARED,
      sharedPostId: currentChat?.shared_post_id || null,
      isAppPublic: currentChat?.is_app_public || false,
      updateAppVisibility: (isPublic) => {
        setCurrentChat(prev => prev ? ({ ...prev, is_app_public: isPublic }) : prev);
      },
      // Optimistic mode/tier flip (2026-09-07, Nitish: an employee-shift switch took 4-5s with no feedback).
      // The composer flips the chip on click and reverts on a failed PUT; the applied values still arrive
      // through the chat_status_update broadcast and overwrite whatever is here.
      updateChatModeTier: (mode, tier) => {
        setCurrentChat(prev => prev ? ({ ...prev, chat_mode: mode, chat_tier: tier }) : prev);
      },
      // In-chat connect cards (2026-09-15, agent_docs/in_chat_connect_cards_design.md §5): a card writes
      // this chat's capability config through the SAME PUTs the editor chips use, but the idle (direct)
      // config path publishes no broadcast, so the card patches the chat record here after its PUT and
      // the chips re-sync from it (their sync effect keys on hc_enabled / browser_enabled / personal_tools_*).
      // CEL-3662 X3: the composer chips patch through here too (one writer), so a card above flips live.
      // Stable identity (useCallback above): the composer's handlers depend on it, and one effect
      // (the Browse legacy-profile migration) depends on those handlers.
      updateChatCapabilities,
      isOwner,
      chatEmployee,
      isSharedChat,
      isViewingSharedChat,
      chatOwnerName,
      loadChat,
      // Live Voice (design v1 §5.5): the chat window refreshes history when a live conversation ends so the spoken
      // rows land; the same refresh the CHAT_INTERIM_MESSAGE refresh_history path already runs.
      refreshChatHistory,
      sendMessage,
      deleteLetterboxMessage,
      applyDeletedMessage,
      pausedMailboxPrompt,
      clearPausedMailboxPrompt: () => setPausedMailboxPrompt(null)
    }}>
      {children}
    </CurrentChatContext.Provider>
  );
};

export const useCurrentChat = () => useContext(CurrentChatContext);
